import type { NoteName } from 'shared/definitions/musicTheory/notesAndAccidentals';
import type { IntervalName } from 'shared/definitions/musicTheory/intervals';
import InteractivePrototype from './interactivePrototype';
import type { InteractiveOptions } from './interactivePrototype';
import utils from '../utils';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import Note from '../uTheory.Notation/note';
import Interval from '../uTheory.Notation/interval';
import type { InteractiveRegistryEntry } from '../uTheory.Skills/interactiveRegistry';

export interface CompoundIntervalsOptions extends InteractiveOptions {
  lowestNote?: NoteName;
  highestNote?: NoteName;
}

interface CompoundIntervalsQuestion {
  interval: Interval;
  answer: number;
  text: [string, number];
  skills: string;
}

export default class CompoundIntervals extends InteractivePrototype {
  declare currentQuestion: CompoundIntervalsQuestion;

  declare questions: CompoundIntervalsQuestion[];

  // Public constructor
  constructor(displayDiv: HTMLDivElement, options: CompoundIntervalsOptions) {
    super(displayDiv, options);
    this.layout = {
      instructions: { maxPercent: 40, noWrap: true },
    };
    this.targetSpeedFromSkills = ['simple_and_compound_intervals'];
    this.settings = {
      instructions: {
        ariaLive: 'assertive',
        ariaLiveRegion: 0,
      },
      notation: false,
      letters: {
        numberButtons: { level: 2 },
        intervalQualityButtons: false,
        checkButton: true,
      },
      checkMark: true,
      player: {},
      oneTryOnly: false,
      progressTracker: true,
    };
    this.init(displayDiv, options);
  }
  // Overriding these methods / properties:

  private answer: {
    genericInterval: number | false;
  } = {
    genericInterval: false,
  };

  init(displayDiv: HTMLDivElement, options: CompoundIntervalsOptions) {
    if (options.lowestNote === undefined) {
      options.lowestNote = 'C2';
    }

    if (options.highestNote === undefined) {
      options.highestNote = 'C6';
    }

    return super.init(displayDiv, options);
  }

  generateQuestions(questions = this.options.questions) {
    const options = this.options;
    this.questions = options.drill
      ? this.generateDrillQuestions(options)
      : this.generateManualQuestions(questions!);

    this.questionPrompt =
      "A {question} is<br /><span id='compoundIntervalAOrAn'>a(n)</span><span id='compoundIntervalAnswerSpan'>___</span>";

    if (this.options.testMode !== true) {
      this.questionPrompt += `<br /><small>#{question} of ${this.questions!.length}</small>`;
    }
  }

  /*
   * converts a questionString to a question object
   */
  processQuestion(question: IntervalName, index: number): CompoundIntervalsQuestion {
    const interval = new Interval(question);
    const compoundOrSimple =
      interval.compound === true ? interval.simpleGenericInterval : interval.genericInterval + 7;
    const skills = this.calculateSkill();

    const questionText = `${
      interval.compound ? 'simple' : 'compound'
    } ${interval.ordinalGenericName()}`;

    return {
      interval,
      answer: compoundOrSimple,
      text: [questionText, index + 1],
      skills,
    };
  }

  generateManualQuestions(questions: IntervalName[]) {
    questions = questions || this.options.questions;
    return questions.map((q, i) => this.processQuestion(q, i));
  }

  generateDrillQuestions(options: CompoundIntervalsOptions): CompoundIntervalsQuestion[] {
    const generator = new QuestionGenerator();
    const maxQuestions = options.maxQuestions || 10;

    return generator
      .simple_and_compound_with_generics(maxQuestions)
      .map(({ interval }) => interval.toString())
      .map(this.processQuestion.bind(this));
  }

  calculateSkill() {
    return 'simple_and_compound_intervals';
  }

  updateAnswerText(aOrAn: string, text: string, doNotAnnounce = false) {
    const aOrAnSpan = this.displayDiv.querySelector('#compoundIntervalAOrAn')!;
    const answerSpan = this.displayDiv.querySelector('#compoundIntervalAnswerSpan')!;

    if (doNotAnnounce) {
      answerSpan.removeAttribute('aria-live');
    } else {
      answerSpan.setAttribute('aria-live', 'polite');
    }
    aOrAnSpan.innerHTML = `${aOrAn} `;
    answerSpan.innerHTML = text;
  }

  newQuestion() {
    this.wrongAnswerCount = 0;
    super.newQuestion();
    this.clearAnswer();
  }

  newCheckpointQuestion(questionString: string) {
    return this.loadingPromise.then(() => {
      this.generateQuestions([questionString]);
      this.questionNumber = 0;
      this.newQuestion();
      return Promise.resolve(this.resize());
    });
  }

  addListeners() {
    this.letterPanel!.addNumberCallback(this.addIntervalNameToAnswer, this);
    this.letterPanel!.addCheckCallback(this.checkAnswer, this);
  }

  addIntervalNameToAnswer(generic: string) {
    this.answer.genericInterval = Number(generic);

    return this.displayAnswer();
  }

  displayAnswer() {
    let ordinal = this.answer.genericInterval ? utils.ordinal(this.answer.genericInterval) : '';
    const aOrAn = this.answer.genericInterval ? utils.aOrAn(this.answer.genericInterval) : '';
    if (ordinal === '8th') ordinal = '8ve';
    if (ordinal === '1st') ordinal = 'Unison';

    const answer = ordinal;

    if (answer === '') {
      this.clearAnswer();
    } else {
      this.updateAnswerText(aOrAn, answer);
    }
  }

  checkAnswer() {
    if (this.rightAnswerDelay) return undefined;

    if (!this.answer.genericInterval) {
      return this.checkMark!.warn('You must provide an interval number first.');
    }

    const testMode = this.optionsTrue('testMode');
    const questionAnswer = this.currentQuestion.answer;
    const userAnswer = this.answer.genericInterval;

    const correct = questionAnswer === userAnswer;

    if (testMode === true) {
      this.checkpointAnswer(correct);
      return undefined;
    }

    if (correct === true) {
      this.getAnswerAndData(true);
      this.rightAnswer(1500);
    } else {
      this.getAnswerAndData(false);
      this.wrongAnswer();
    }
    return undefined;
  }

  clearAnswer() {
    this.answer.genericInterval = false;

    this.updateAnswerText('a(n)', '___', true);
  }

  getAnswerAndData(correct: boolean) {
    const question = this.currentQuestion;
    const questionText = `A ${question.text[0]} is a(n) __.`;
    const correctAnswer = question.answer;
    const answerAndData = {
      question: `${question.interval.genericInterval}`,
      questionType: 'CompoundIntervals',
      questionText,
      studentAnswerText: `${this.answer.genericInterval}`,
      correctAnswerText: `${correctAnswer}`,
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: [this.currentQuestion.skills],
    };

    return answerAndData;
  }

  gameOver() {
    this.instructionsDiv!.innerHTML = 'Nice work!';
    this.instructionsDiv!.setAttribute('role', 'alert');
    this.displayDiv.removeChild(this.lettersDiv!);

    super.gameOver();
  }

  playAnswer() {
    if (this.options.testMode) return;
    const C = new Note('C3');
    const simpleGeneric = this.currentQuestion.interval.simpleGenericInterval;
    const compoundGeneric = simpleGeneric + 7;
    const simpleNote = C.getNoteAtGeneric(simpleGeneric);
    const compoundNote = C.getNoteAtGeneric(compoundGeneric);

    const interval = this.currentQuestion.interval;
    if (interval.compound) {
      this.player!.play(C, { startTime: 0, stopTime: 1 });
      this.player!.play(compoundNote, { startTime: 0.33, stopTime: 1 });
      this.player!.play(C, { startTime: 1, stopTime: 2 });
      this.player!.play(simpleNote, { startTime: 1.33, stopTime: 2 });
    } else {
      this.player!.play(C, { startTime: 0, stopTime: 1 });
      this.player!.play(simpleNote, { startTime: 0.33, stopTime: 1 });
      this.player!.play(C, { startTime: 1, stopTime: 2 });
      this.player!.play(compoundNote, { startTime: 1.33, stopTime: 2 });
    }
  }

  registry: InteractiveRegistryEntry = {
    description: 'Simple and Compound Intervals',
    skills: ['simple_and_compound_intervals'],
  };
}
